import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewChecked,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { selectWorkspaceParams } from '@gestor-web/@core/store/selectors/operadoras.selectors';
import { selectIsAdmin } from '@gestor-web/@core/store/selectors/user-profile.selectors';

export interface CompetenciaRange {
  inicial: string;
  final: string;
}

const COMPETENCIA_MATCHER = /^(\d{4})[-/]?(\d{2})$/;

@Component({
    selector: 'bl-competencia',
    templateUrl: './competencia.component.html',
    styleUrls: ['./competencia.component.scss'],
    standalone: false
})
export class CompetenciaComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('competencia') calendar: any;
  @Input() competencia?: string;
  @Input() competenciaFinal?: string;
  @Input() inputClass?: string;
  @Input() hideYearSelector: boolean = false;
  @Input() disabled: boolean = false;
  @Input() useDefaultRange: boolean = true;
  @Input() allowedCompetencias: CompetenciaRange[] = [];
  @Input() label: string = 'Competência(s)'; // Permite customizar o label exibido
  @Output() apply = new EventEmitter<string[]>();

  isAdmin: boolean = false;

  innerValue: Date[] | null = null; // Date[] or string[]
  selectedCompetencias: Date[] | null = null;

  minDate: Date | null = null;
  maxDate: Date | null = null;
  disabledDates: Date[] | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private elementRef: ElementRef,
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectIsAdmin)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAdmin => {
        this.isAdmin = isAdmin;
        this.processIntervals();
      });

    if (this.allowedCompetencias.length > 0) {
      return;
    }

    if (!this.useDefaultRange) {
      return;
    }

    this.store
      .select(selectWorkspaceParams)
      .pipe(
        filter(params => !!params?.competencia_minima && !!params?.competencia_maxima),
        distinctUntilChanged(
          (a, b) => a.competencia_minima === b.competencia_minima && a.competencia_maxima === b.competencia_maxima,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(params => {
        this.allowedCompetencias = [
          {
            inicial: `${params.competencia_minima}`,
            final: `${params.competencia_maxima}`,
          },
        ];
        this.processIntervals();
      });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.processIntervals();

    if (!this.competencia || !this.competenciaFinal) {
      this.innerValue = null;
      this.selectedCompetencias = null;
      return;
    }

    const competenciaInicial = this.extractDate(this.competencia);
    const competenciaFinal = this.extractDate(this.competenciaFinal);

    if (!competenciaInicial || !competenciaFinal) {
      this.innerValue = null;
      this.selectedCompetencias = null;
      return;
    }

    this.innerValue = [new Date(competenciaInicial), new Date(competenciaFinal)];
    this.selectedCompetencias = [new Date(competenciaInicial), new Date(competenciaFinal)];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  emitValue() {
    if (this.disabled || !this.selectedCompetencias?.[0] || !this.selectedCompetencias?.[1]) {
      return;
    }

    const competenciaInicial = this.formatDateToCompetencia(this.selectedCompetencias[0]);
    const competenciaFinal = this.formatDateToCompetencia(this.selectedCompetencias[1]);

    if (!competenciaInicial || !competenciaFinal) {
      return;
    }

    this.innerValue = [new Date(this.selectedCompetencias[0]), new Date(this.selectedCompetencias[1])];
    this.apply.emit([competenciaInicial, competenciaFinal]);
  }

  cancelSelection() {
    this.selectedCompetencias = this.innerValue ? [...this.innerValue] : null;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.cancelSelection();
    }
  }

  private extractDate(competencia: string, endOfMonth = false): Date | null {
    let matcher = competencia.match(COMPETENCIA_MATCHER);
    if (!matcher) {
      return null;
    }

    const year = +matcher[1];
    const monthIndex = +matcher[2] - 1;

    if (!endOfMonth) {
      return new Date(year, monthIndex, 1, 0, 0, 0, 0);
    }

    return new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
  }

  private formatDateToCompetencia(date: Date): string | null {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return null;
    }

    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${date.getFullYear()}${month}`;
  }

  private processIntervals() {
    // console.log('processIntervals - competencias', competencias);

    const processedIntervals: CompetenciaRange[] = [];
    let min: string | null = null;
    let max: string | null = null;

    // Processa lista de competencias...
    if (this.allowedCompetencias && this.allowedCompetencias.length) {
      for (const competencia of this.allowedCompetencias) {
        // Captura minimo e máximo
        if (min == null || min > competencia.inicial) {
          min = competencia.inicial;
        }
        if (max == null || max < competencia.final) {
          max = competencia.final;
        }

        // Compara com intervalos existentes...
        let pLesser = -1;
        let pHigher = -1;
        let pMin = -1;
        let pMax = -1;

        for (let i = 0; i < processedIntervals.length; i++) {
          // Intervalo menor que a competencia não adjacente: pula para próximo
          if (this.compareCompetencia(processedIntervals[i].final, competencia.inicial) < -1) {
            pLesser = i;
            continue;
          }

          // Intervalo maior que a competencia não adjacente: sai do loop
          if (this.compareCompetencia(processedIntervals[i].inicial, competencia.final) > 1) {
            pHigher = i;
            break;
          }

          // Primeiro intervalo coincidente (mímino)
          if (pMin < 0) {
            pMin = i;
          }

          // Ultimo intervalo coincidente (máximo)
          pMax = i;
        }

        // console.log('Comparison:', competencia, pLess, pMin, pMax, pMore);

        // Competencia isolada: novo intervalo
        if (pMin < 0) {
          const newInterval: CompetenciaRange = {
            inicial: competencia.inicial,
            final: competencia.final,
          };

          // Competencia no ínicio
          if (pLesser < 0) {
            processedIntervals.unshift(newInterval);
          }

          // Competencia depois do menor
          else {
            processedIntervals.splice(pLesser + 1, 0, newInterval);
          }
        }

        // Competencia engloba intervalos existentes
        else {
          const minInterval = processedIntervals[pMin];
          const maxInterval = processedIntervals[pMax];
          const minInicial = competencia.inicial <= minInterval.inicial ? competencia.inicial : minInterval.inicial;
          const maxFinal = competencia.final >= maxInterval.final ? competencia.final : maxInterval.final;

          // Atualiza primeiro intervalo para englobar tudo
          minInterval.inicial = minInicial;
          minInterval.final = maxFinal;

          // Remove demais intervalos que já foram contabilizados no primeiro
          if (pMax > pMin) {
            processedIntervals.splice(pMin + 1, pMax - pMin);
          }
        }

        // console.log('processedIntervals:', processedIntervals.map(p => `[${p.inicial},${p.final}]`).join(','));
      }
    }

    if (this.isAdmin) {
      this.minDate = null;
      this.maxDate = null;
      this.disabledDates = null;
    } else {
      this.minDate = min != null ? this.extractDate(min) : null;
      this.maxDate = max != null ? this.extractDate(max, true) : null;

      const disabledDates: Date[] = [];
      for (let i = 0; i < processedIntervals.length - 1; i++) {
        const lastIntervalDate = this.extractDate(processedIntervals[i].final, true);
        const nextIntervalDate = this.extractDate(processedIntervals[i + 1].inicial);

        if (!lastIntervalDate || !nextIntervalDate) {
          continue;
        }

        let date = new Date(lastIntervalDate);
        date.setDate(date.getDate() + 1);
        while (date < nextIntervalDate) {
          disabledDates.push(new Date(date));
          date.setDate(date.getDate() + 1);
        }
      }

      this.disabledDates = disabledDates;
    }
  }

  private compareCompetencia(c1: string, c2: string): number {
    const m1 = c1.match(COMPETENCIA_MATCHER);
    const m2 = c2.match(COMPETENCIA_MATCHER);

    if (!m1 || !m2) {
      return undefined;
    }

    const diffYear = parseInt(m1[1], 10) - parseInt(m2[1], 10);
    const diffMonth = parseInt(m1[2], 10) - parseInt(m2[2], 10);

    return diffYear * 12 + diffMonth;
  }

  ngAfterViewChecked(): void {
    this.applyAdminStyles();
  }

  private applyAdminStyles(): void {
    if (
      !this.isAdmin ||
      !this.calendar ||
      !this.calendar.overlayVisible ||
      !this.calendar.el ||
      !this.calendar.el.nativeElement
    ) {
      return;
    }

    const container =
      this.calendar.el.nativeElement.querySelector('.p-datepicker') || document.querySelector('.p-datepicker');
    if (!container) return;

    let year = this.calendar.currentYear;
    if (!year) {
      const yearEl = container.querySelector('.p-datepicker-year');
      if (yearEl) {
        year = parseInt(yearEl.textContent || '', 10);
      }
    }
    if (!year) return;

    const monthElements = container.querySelectorAll('.p-datepicker-month');
    if (monthElements.length !== 12) return;

    monthElements.forEach((el: HTMLElement, index: number) => {
      const isVisualDisabled = this.isMonthOutOfrange(year, index);

      if (isVisualDisabled && !el.classList.contains('admin-disabled-month')) {
        el.classList.add('admin-disabled-month');
      } else if (!isVisualDisabled && el.classList.contains('admin-disabled-month')) {
        el.classList.remove('admin-disabled-month');
      }
    });
  }

  private isMonthOutOfrange(year: number, monthIndex: number): boolean {
    const compStr = `${year}${String(monthIndex + 1).padStart(2, '0')}`;

    if (!this.allowedCompetencias || this.allowedCompetencias.length === 0) return false;

    let isAllowed = false;
    for (const range of this.allowedCompetencias) {
      const inicial = range.inicial.replace(/[-/]/g, '');
      const final = range.final.replace(/[-/]/g, '');

      if (compStr >= inicial && compStr <= final) {
        isAllowed = true;
        break;
      }
    }

    return !isAllowed;
  }
}
